import {
  clampSceneValueV1,
  usableSceneMarketValueV1,
  type ScenePositionInputV1,
  type SceneRoomSignalsV1,
  type SceneWeatherV1,
} from "./sceneRulesV1";

export const SCENE_RULE_VERSION_V3 = 3 as const;
export const SCENE_V3_ROOM_RETURN_CURVE_DIVISOR = 2.5;
export const SCENE_V3_PARTY_RETURN_THRESHOLD_PCT = 2;
export const SCENE_V3_PANIC_RETURN_THRESHOLD_PCT = -2;
export const SCENE_V3_PANIC_EFFECT_LEAD_RETURN_PCT = -1.5;

export function roomIntensityForAbsoluteReturnPctV3(
  absoluteReturnPct: number,
): number {
  return Math.tanh(
    Math.abs(absoluteReturnPct) / SCENE_V3_ROOM_RETURN_CURVE_DIVISOR,
  );
}

export const SCENE_V3_PARTY_INTENSITY_THRESHOLD =
  roomIntensityForAbsoluteReturnPctV3(
    SCENE_V3_PARTY_RETURN_THRESHOLD_PCT,
  );
export const SCENE_V3_PANIC_STORM_INTENSITY_THRESHOLD =
  roomIntensityForAbsoluteReturnPctV3(
    SCENE_V3_PANIC_RETURN_THRESHOLD_PCT,
  );
export const SCENE_V3_PANIC_EFFECT_LEAD_STORM_INTENSITY =
  roomIntensityForAbsoluteReturnPctV3(
    SCENE_V3_PANIC_EFFECT_LEAD_RETURN_PCT,
  );

export function panicIntensityFromStormIntensityV3(
  stormIntensity: number,
): number {
  return clampSceneValueV1(
    (
      stormIntensity - SCENE_V3_PANIC_EFFECT_LEAD_STORM_INTENSITY
    ) / (1 - SCENE_V3_PANIC_EFFECT_LEAD_STORM_INTENSITY),
    0,
    1,
  );
}

/**
 * V3 preserves the V2 size curve while moving room milestones to memorable,
 * symmetric portfolio returns: party/panic at ±2%, extremes at ±4%.
 */
export function deriveRoomSignalsV3(
  positions: readonly ScenePositionInputV1[],
): SceneRoomSignalsV1 {
  const totalMarketValue = positions.reduce(
    (sum, position) =>
      sum + usableSceneMarketValueV1(position.marketValue),
    0,
  );

  if (totalMarketValue <= 0) {
    return {
      valence: 0,
      knownReturnPct: 0,
      dataCoverage: 0,
      partyIntensity: 0,
      stormIntensity: 0,
      panicIntensity: 0,
      weather: "clear",
      totalMarketValue: 0,
    };
  }

  let dataCoverage = 0;
  let weightedReturnContribution = 0;
  for (const position of positions) {
    if (
      !position.returnUsable ||
      position.dailyReturnPct === null ||
      !Number.isFinite(position.dailyReturnPct)
    ) {
      continue;
    }
    const weight =
      usableSceneMarketValueV1(position.marketValue) / totalMarketValue;
    dataCoverage += weight;
    weightedReturnContribution += weight * position.dailyReturnPct;
  }

  const knownReturnPct =
    dataCoverage > 0 ? weightedReturnContribution / dataCoverage : 0;
  const valence =
    dataCoverage > 0
      ? clampSceneValueV1(
          dataCoverage
            * Math.tanh(
              knownReturnPct / SCENE_V3_ROOM_RETURN_CURVE_DIVISOR,
            ),
          -1,
          1,
        )
      : 0;
  const partyIntensity = Math.max(valence, 0);
  const stormIntensity = Math.max(-valence, 0);
  const panicIntensity =
    panicIntensityFromStormIntensityV3(stormIntensity);

  const weather: SceneWeatherV1 =
    valence >= SCENE_V3_PARTY_INTENSITY_THRESHOLD
      ? "party"
      : stormIntensity >= SCENE_V3_PANIC_STORM_INTENSITY_THRESHOLD
        ? "panic"
        : valence < 0
          ? "storm"
          : "clear";

  return {
    valence,
    knownReturnPct,
    dataCoverage,
    partyIntensity,
    stormIntensity,
    panicIntensity,
    weather,
    totalMarketValue,
  };
}
